/**
 * A moderator's "Stop screen share" on somebody else's share, from the click
 * until that share is gone (BUG-000782).
 *
 * The stop is carried out by the presenter's own browser: the channel relays
 * `moderate:stop_screenshare` to it, and the share leaves the room only once
 * that browser has closed its producers. Usually well under a second; a dev
 * capture took 3.5 s. The button was send-and-forget and showed nothing for all
 * of it, so hosts clicked it again, three or four times, and took the click
 * that happened to coincide with the share ending for the one that worked.
 *
 * A stop is now pending from the click until one of:
 * - the share ends. The only real confirmation: the channel's `ok` says it
 *   relayed the command, not that the presenter acted on it;
 * - the channel refuses it, or it could not be sent (a failure, at once);
 * - STOP_SHARE_WAIT_MS pass with the share still up (a failure).
 * While it is pending the buttons read "Stopping…" and a second click sends
 * nothing.
 */

/** How long the share may stay up after a stop before it is reported as failed. */
export const STOP_SHARE_WAIT_MS = 10_000

/** Pending stops: the presenter's peerId → when the stop was sent (ms). */
export type PendingShareStops = ReadonlyMap<string, number>

/**
 * Signaling's ids are bare user ids; an SFU peerId may carry a `__<session>`
 * suffix. The stage keys a share by the SFU's id and the roster by its own, so
 * a stop started from one must be found from the other.
 */
const base = (id: string) => id.split('__')[0]

function keyOf(pending: PendingShareStops, peerId: string): string | undefined {
  if (pending.has(peerId)) return peerId
  const want = base(peerId)
  for (const key of pending.keys()) if (base(key) === want) return key
  return undefined
}

export function isStopPending(pending: PendingShareStops, peerId: string): boolean {
  return keyOf(pending, peerId) !== undefined
}

/** A stop sent now. Unchanged when one for this presenter is already pending. */
export function startStop(pending: PendingShareStops, peerId: string, now: number): PendingShareStops {
  if (isStopPending(pending, peerId)) return pending
  return new Map(pending).set(peerId, now)
}

/** The stop for this presenter, settled either way. Unchanged when there is none. */
export function endStop(pending: PendingShareStops, peerId: string): PendingShareStops {
  const key = keyOf(pending, peerId)
  if (key === undefined) return pending
  const next = new Map(pending)
  next.delete(key)
  return next
}

/**
 * Drops every stop whose share has ended: the stop worked. Unchanged when every
 * pending presenter is still sharing.
 */
export function settleEndedShares(
  pending: PendingShareStops,
  sharingPeerIds: readonly string[],
): PendingShareStops {
  if (pending.size === 0) return pending
  const sharing = new Set(sharingPeerIds.map(base))
  let next: Map<string, number> | null = null
  for (const key of pending.keys()) {
    if (sharing.has(base(key))) continue
    next ??= new Map(pending)
    next.delete(key)
  }
  return next ?? pending
}

/** The presenters whose share is still up STOP_SHARE_WAIT_MS after the stop. */
export function overdueStops(
  pending: PendingShareStops,
  now: number,
  waitMs: number = STOP_SHARE_WAIT_MS,
): string[] {
  const out: string[] = []
  for (const [key, sentAt] of pending) if (now - sentAt >= waitMs) out.push(key)
  return out
}

/** When the oldest pending stop becomes overdue, or null when none is pending. */
export function nextDeadline(
  pending: PendingShareStops,
  waitMs: number = STOP_SHARE_WAIT_MS,
): number | null {
  let first: number | null = null
  for (const sentAt of pending.values()) if (first === null || sentAt < first) first = sentAt
  return first === null ? null : first + waitMs
}

/**
 * Why a stop failed:
 * - `host_protected`: a co-host aimed it at the host, whose share only the
 *   host may stop;
 * - `refused`: any other refusal, e.g. `host_only` from someone who stopped
 *   being a moderator;
 * - `not_sent`: the socket was not open, so it never left this browser;
 * - `overdue`: relayed, but the share was still up STOP_SHARE_WAIT_MS later.
 */
export type StopShareFailure = 'host_protected' | 'refused' | 'not_sent' | 'overdue'

/**
 * What the channel's answer means for a stop: a failure, or null to keep
 * waiting for the share to end.
 *
 * A reply that timed out is not a failure by itself. The command may well have
 * arrived and the presenter acted on it; only the answer is late. The share
 * ending, or the deadline, decides.
 */
export function stopReplyFailure(reply: {
  status: string
  response?: Record<string, unknown>
}): Exclude<StopShareFailure, 'overdue'> | null {
  if (reply.status === 'dropped') return 'not_sent'
  if (reply.status !== 'error') return null
  return reply.response?.reason === 'host_protected' ? 'host_protected' : 'refused'
}
